/**
 * Tenant Context
 * Manages current organization and school context
 */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { schoolsAPI, organizationsAPI } from '../api';
import { updateSidebarData, updateSelectedSchool } from '../components/dashboard/StaticSidebar';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [currentSchool, setCurrentSchool] = useState(null);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Use refs to prevent re-renders from causing infinite loops
  const hasLoadedRef = useRef(false);
  const loadingRef = useRef(false);

  // Load tenant data - only runs when authentication changes
  useEffect(() => {
    if (!isAuthenticated || hasLoadedRef.current || loadingRef.current) {
      return;
    }

    const loadData = async () => {
      try {
        loadingRef.current = true;
        setLoading(true);
        setError(null);

        console.log('🔄 TenantContext: Loading tenant data...');
        let token = await getAccessTokenSilently();

        // Try to get organizations
        let organizations;
        try {
          organizations = await organizationsAPI.getMyOrganization(token);
          console.log('✅ TenantContext: Organizations loaded:', organizations?.length || 0);
        } catch (orgError) {
          // If we get "No organization information found" error, try refreshing the token
          if (orgError.message?.includes('No organization information found')) {
            console.log('🔄 TenantContext: Token may be stale, refreshing and retrying...');
            try {
              // Force fresh token from Auth0
              token = await getAccessTokenSilently({ cacheMode: 'off' });
              organizations = await organizationsAPI.getMyOrganization(token);
              console.log('✅ TenantContext: Organizations loaded after token refresh:', organizations?.length || 0);
            } catch (retryError) {
              console.error('❌ TenantContext: Still failed after token refresh:', retryError);
              throw retryError;
            }
          } else {
            throw orgError;
          }
        }
        
        if (organizations && organizations.length > 0) {
          setCurrentOrganization(organizations[0]);
        }

        // Get schools (using the potentially refreshed token)
        const schools = await schoolsAPI.getSchools(token);
        console.log('✅ TenantContext: Schools loaded:', schools?.length || 0);
        setAvailableSchools(schools || []);

        // Update sidebar cache immediately
        updateSidebarData(
          organizations?.[0] || null, 
          schools || [], 
          null // Will be set by URL effect
        );

        hasLoadedRef.current = true;
        
      } catch (err) {
        console.error('❌ TenantContext: Error loading data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadData();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Handle URL-based school selection - separate from loading
  useEffect(() => {
    if (!availableSchools.length || !currentOrganization) return;

    const urlParts = location.pathname.split('/');
    
    // If URL contains school context, set it
    if (urlParts.includes('school')) {
      const schoolIndex = urlParts.indexOf('school');
      if (schoolIndex + 1 < urlParts.length) {
        const schoolUniqueId = urlParts[schoolIndex + 1];
        const school = availableSchools.find(s => s.unique_id === schoolUniqueId || s.unique_id === schoolUniqueId);
        
        if (school) {
          console.log('🎯 TenantContext: Setting school from URL:', school.name);
          setCurrentSchool(school);
          updateSelectedSchool(school);
        }
      }
    } else if (!location.pathname.includes('school') && availableSchools.length > 0) {
      // Auto-select first school only if not in school context
      setCurrentSchool(prevSchool => {
        if (!prevSchool) {
          console.log('🎯 TenantContext: Auto-selecting first school:', availableSchools[0].name);
          updateSelectedSchool(availableSchools[0]);
          return availableSchools[0];
        }
        return prevSchool;
      });
    }
  }, [location.pathname, availableSchools, currentOrganization]);

  // Actions
  const switchSchool = (school) => {
    console.log('🔄 TenantContext: Switching to school:', school.name);
    setCurrentSchool(school);
    updateSelectedSchool(school);
    
    if (currentOrganization && school) {
      const schoolId = school.unique_id || school.unique_id;
      navigate(`/org/${currentOrganization.unique_id}/school/${schoolId}/dashboard`);
    }
  };

  const goToOrganizationDashboard = () => {
    setCurrentSchool(null);
    updateSelectedSchool(null);
    if (currentOrganization) {
      navigate(`/org/${currentOrganization.unique_id}/dashboard`);
    }
  };

  const refreshSchools = async () => {
    try {
      console.log('🔄 TenantContext: Refreshing schools data...');
      const token = await getAccessTokenSilently();
      const schools = await schoolsAPI.getSchools(token);
      console.log('✅ TenantContext: Schools refreshed:', schools?.length || 0);
      
      setAvailableSchools(schools || []);
      
      // Update sidebar cache with new schools data
      updateSidebarData(
        currentOrganization, 
        schools || [], 
        currentSchool // Keep current school selection
      );
      
      return schools;
    } catch (err) {
      console.error('❌ TenantContext: Error refreshing schools:', err);
      throw err;
    }
  };

  const getTenantUrl = (path, schoolSpecific = false) => {
    if (!currentOrganization) return path;

    if (schoolSpecific && currentSchool) {
      const schoolId = currentSchool.unique_id || currentSchool.unique_id;
      return `/org/${currentOrganization.unique_id}/school/${schoolId}${path}`;
    }
    
    return `/org/${currentOrganization.unique_id}${path}`;
  };

  const hasPermission = () => {
    return !!currentOrganization;
  };

  const getContextType = () => {
    if (currentSchool) return 'school';
    if (currentOrganization) return 'organization';
    return 'none';
  };

  // Stable context value
  const value = {
    // State
    currentOrganization,
    currentSchool,
    availableSchools,
    loading,
    error,

    // Actions
    switchSchool,
    goToOrganizationDashboard,
    refreshSchools,
    setCurrentSchool,

    // Utilities
    getTenantUrl,
    hasPermission,
    getContextType,

    // Computed values
    isOrganizationContext: getContextType() === 'organization',
    isSchoolContext: getContextType() === 'school',
    hasSchools: availableSchools.length > 0
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantProvider;
